import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtUSD, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { ArrowDownLeft, ArrowUpRight, CheckCircle2, Landmark } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  declined: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

const FLOW = [
  { key: "requested", label: "Requested" },
  { key: "approved", label: "Approved" },
  { key: "completed", label: "Completed" },
];

function FundsTimeline({ status }) {
  // Terminal off-path states get a two-step timeline.
  if (status === "declined" || status === "cancelled") {
    const isDeclined = status === "declined";
    return (
      <div className="mt-4 flex items-center gap-2" data-testid="funds-timeline">
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="size-2.5 rounded-full bg-[#0EA5A4]" />
          <span className="text-[11px] text-foreground">Requested</span>
        </div>
        <div className={`h-px flex-1 ${isDeclined ? "bg-red-300" : "bg-zinc-300"}`} />
        <div className="flex items-center gap-1.5 shrink-0">
          <span className={`size-2.5 rounded-full ${isDeclined ? "bg-red-500" : "bg-zinc-400"}`} />
          <span className={`text-[11px] ${isDeclined ? "text-red-600" : "text-zinc-500"}`}>{isDeclined ? "Declined" : "Cancelled"}</span>
        </div>
      </div>
    );
  }
  const idx = status === "pending" ? 0 : status === "approved" ? 1 : 2;
  return (
    <div className="mt-4 flex items-center gap-2" data-testid="funds-timeline">
      {FLOW.map((step, i) => (
        <div key={step.key} className={`flex items-center gap-2 ${i < FLOW.length - 1 ? "flex-1" : "shrink-0"}`}>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className={`size-2.5 rounded-full ${i <= idx ? "bg-[#0EA5A4]" : "bg-border"}`} />
            <span className={`text-[11px] ${i <= idx ? "text-foreground" : "text-muted-foreground"}`}>{step.label}</span>
          </div>
          {i < FLOW.length - 1 && <div className={`h-px flex-1 ${i < idx ? "bg-[#0EA5A4]" : "bg-border"}`} />}
        </div>
      ))}
    </div>
  );
}

export default function Funds() {
  const [type, setType] = useState("deposit");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [requests, setRequests] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const load = () => api.get("/portal/funds-requests").then(({ data }) => setRequests(data.requests)).catch(() => setRequests([]));
  useEffect(() => { load(); }, []);

  const cancelRequest = async (r) => {
    setCancelling(r.request_id);
    try {
      await api.post(`/portal/funds-request/${r.request_id}/cancel`);
      toast.success("Request cancelled.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel the request.");
    } finally { setCancelling(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Please enter an amount greater than 0."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/portal/funds-request", { type, amount: amt, note });
      setConfirmation(data);
      setAmount("");
      setNote("");
      await load();
      toast.success("Request submitted — our team will follow up.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit your request.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6" data-testid="funds-page">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Funds</h1>
        <p className="text-sm text-muted-foreground mt-1">Request to add capital or withdraw. Our team follows up personally to arrange it — <span className="text-foreground">no funds move through this site</span>.</p>
      </div>

      <Card className="rounded-xl p-5 sm:p-6 ring-1 ring-border">
        <form onSubmit={submit} data-testid="funds-form" className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <button type="button" data-testid="funds-type-deposit" onClick={() => setType("deposit")}
              className={`flex items-center justify-center gap-2 h-12 rounded-xl border text-sm font-medium transition-colors ${type === "deposit" ? "border-brand bg-brand/10 text-foreground ring-1 ring-brand/30" : "border-border text-muted-foreground hover:bg-muted"}`}>
              <ArrowDownLeft className="size-4" /> Add funds
            </button>
            <button type="button" data-testid="funds-type-withdrawal" onClick={() => setType("withdrawal")}
              className={`flex items-center justify-center gap-2 h-12 rounded-xl border text-sm font-medium transition-colors ${type === "withdrawal" ? "border-brand bg-brand/10 text-foreground ring-1 ring-brand/30" : "border-border text-muted-foreground hover:bg-muted"}`}>
              <ArrowUpRight className="size-4" /> Withdraw
            </button>
          </div>

          <div className="space-y-2">
            <Label>Amount (USD)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input data-testid="funds-amount-input" type="number" min="0" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="50,000" className="pl-7 font-mono-num" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Note (optional)</Label>
            <Textarea data-testid="funds-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Anything our team should know?" />
          </div>

          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="text-xs text-muted-foreground">This logs a request only — <span className="text-amber-600">simulated</span>, no real money moves.</p>
            <Button data-testid="funds-submit-button" type="submit" disabled={submitting} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0B8E8D] text-white h-11 px-6">
              {submitting ? "Submitting…" : "Submit request"}
            </Button>
          </div>
        </form>

        {confirmation && (
          <div data-testid="funds-confirmation" className="mt-5 rounded-xl bg-brand/[0.06] ring-1 ring-brand/20 p-4 flex gap-3">
            <CheckCircle2 className="size-5 text-brand shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-medium">Request received — reference {confirmation.request_id}</div>
              <div className="text-muted-foreground mt-0.5">We've logged your request to {confirmation.type === "deposit" ? "add" : "withdraw"} {fmtUSD(confirmation.amount)}. A member of our team will follow up to arrange it. Check your Messages for confirmation.</div>
            </div>
          </div>
        )}
      </Card>

      <Card className="rounded-xl ring-1 ring-border overflow-hidden" data-testid="funds-requests-list">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h2 className="font-medium">Your requests</h2>
          <Badge variant="outline" className="rounded-full bg-amber-50 text-amber-700 border-amber-200 text-[11px]">SIMULATED</Badge>
        </div>
        {!requests ? (
          <div className="p-4 space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
        ) : requests.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">No requests yet. Submit one above and our team will follow up.</div>
        ) : (
          <div className="divide-y">
            {requests.map((r) => {
              const isDep = r.type === "deposit";
              return (
                <div key={r.request_id} data-testid="funds-request-row" className="px-5 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`size-9 rounded-lg flex items-center justify-center ${isDep ? "bg-emerald-500/10 text-emerald-600" : "bg-red-500/10 text-red-600"}`}>
                        {isDep ? <ArrowDownLeft className="size-4" /> : <ArrowUpRight className="size-4" />}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-sm capitalize">{isDep ? "Add funds" : "Withdrawal"} · <span className="font-mono-num">{fmtUSD(r.amount)}</span></div>
                        <div className="text-xs text-muted-foreground truncate">{fmtDate(r.created_at)} · Ref {r.request_id}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className={`rounded-full text-[11px] capitalize ${STATUS_STYLES[r.status] || STATUS_STYLES.pending}`}>{r.status}</Badge>
                      {r.status === "pending" && (
                        <Button data-testid="funds-cancel-button" variant="ghost" size="sm" className="rounded-lg h-8 px-2 text-xs text-muted-foreground hover:text-destructive" disabled={cancelling === r.request_id} onClick={() => cancelRequest(r)}>
                          {cancelling === r.request_id ? "…" : "Cancel"}
                        </Button>
                      )}
                    </div>
                  </div>
                  <FundsTimeline status={r.status} />
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
